import { Feature, QuadPosition, TermType, } from "./types";
var datatypes = {
    boolean: "http://www.w3.org/2001/XMLSchema#boolean",
    dateTime: "http://www.w3.org/2001/XMLSchema#dateTime",
    double: "http://www.w3.org/2001/XMLSchema#double",
    decimal: "http://www.w3.org/2001/XMLSchema#decimal",
    integer: "http://www.w3.org/2001/XMLSchema#integer",
    langString: "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString",
    string: "http://www.w3.org/2001/XMLSchema#string"
};
function equals(a, b) {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return a === b;
    }
    if (Object.prototype.hasOwnProperty.call(a, 'equals')) {
        return a.equals(b);
    }
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!(Array.isArray(a) && Array.isArray(b))) {
            return false;
        }
        return equals(a[0], b[0])
            && equals(a[1], b[1])
            && equals(a[2], b[2])
            && equals(a[3], b[3]);
    }
    if (PlainFactory.isQuad(a) || PlainFactory.isQuad(b)) {
        if (!PlainFactory.isQuad(a) || !PlainFactory.isQuad(b)) {
            return false;
        }
        return equals(a.subject, b.subject)
            && equals(a.predicate, b.predicate)
            && equals(a.object, b.object)
            && equals(a.graph, b.graph);
    }
    switch (a.termType) {
        case 'Literal': {
            return a.termType === b.termType
                && a.value === b.value
                && b.datatype === b.datatype
                && b.language === b.language;
        }
        default:
            if (!a.termType || !b.termType) {
                return false;
            }
            return a.termType === b.termType
                && a.value === b.value;
    }
}
var RDFBase = {
    equals: function (other) {
        return equals(this, other);
    },
    /* rdflib compat */
    toCanonical: function () {
        return this;
    }
};
/**
 * Plain JS/functional implementation of the RDF/JS: Data model specification, limited to a strict
 * rdf subset (no Variable, no Literal as predicate, etc.).
 */
var PlainFactory = /** @class */ (function () {
    function PlainFactory(opts) {
        var _a;
        if (opts === void 0) { opts = {}; }
        this.bnIndex = opts.bnIndex || 0;
        this.supports = opts.supports || (_a = {},
            _a[Feature.collections] = false,
            _a[Feature.defaultGraphType] = false,
            _a[Feature.equalsMethod] = false,
            _a[Feature.identity] = false,
            _a[Feature.nodeLookup] = false,
            _a[Feature.variableType] = false,
            _a);
    }
    /**
     * Checks if the object {obj} is a Quad.
     */
    PlainFactory.isQuad = function (obj) {
        if (Array.isArray(obj) || !obj) {
            return false;
        }
        return !!(Object.prototype.hasOwnProperty.call(obj, "subject")
            && Object.prototype.hasOwnProperty.call(obj, "predicate")
            && Object.prototype.hasOwnProperty.call(obj, "object"));
    };
    PlainFactory.prototype.namedNode = function (value) {
        var term = Object.create(RDFBase);
        term.termType = TermType.NamedNode;
        term.value = value;
        return term;
    };
    PlainFactory.prototype.blankNode = function (value) {
        var term = Object.create(RDFBase);
        term.termType = TermType.BlankNode;
        term.value = value || "b" + ++this.bnIndex;
        return term;
    };
    PlainFactory.prototype.literal = function (value, languageOrDatatype) {
        if (typeof value !== "string") {
            return this.parseLiteral(value);
        }
        var term = Object.create(RDFBase);
        term.termType = TermType.Literal;
        // Perf: Always set all properties to prevent the creation of unnecessary HiddenClass objects.
        term.language = typeof languageOrDatatype === "string" ? languageOrDatatype : undefined;
        term.datatype = typeof languageOrDatatype === "string"
            ? this.namedNode(datatypes.langString)
            : languageOrDatatype || this.namedNode(datatypes.string);
        term.value = value;
        return term;
    };
    PlainFactory.prototype.defaultGraph = function () {
        return this.namedNode("");
    };
    /**
     * Create an RDF statement in object form.
     * @param subject The subject of the statement
     * @param predicate The predicate of the statement
     * @param object The object of the statement
     * @param graph The graph of the statement
     */
    PlainFactory.prototype.quad = function (subject, predicate, object, graph) {
        return {
            subject: subject,
            predicate: predicate,
            object: object,
            graph: graph || this.defaultGraph()
        };
    };
    PlainFactory.prototype.isQuad = function (obj) {
        return PlainFactory.isQuad(obj);
    };
    /**
     * * Returns an RDF statement in array form.
     * @param subject The subject of the statement
     * @param predicate The predicate of the statement
     * @param object The object of the statement
     * @param graph The graph of the statement
     */
    PlainFactory.prototype.quadruple = function (subject, predicate, object, graph) {
        return [
            subject,
            predicate,
            object,
            graph || this.defaultGraph(),
        ];
    };
    PlainFactory.prototype.fromTerm = function (original) {
        if (original.termType === "Literal") {
            return this.literal(original.value, original.language || original.datatype);
        }
        return {
            termType: original.termType,
            value: original.value
        };
    };
    PlainFactory.prototype.fromQuad = function (original) {
        return {
            subject: original.subject,
            predicate: original.predicate,
            object: original.object,
            graph: original.graph || this.defaultGraph()
        };
    };
    PlainFactory.prototype.fromQdr = function (original) {
        return {
            subject: original[0],
            predicate: original[1],
            object: original[2],
            graph: original[3] || this.defaultGraph()
        };
    };
    PlainFactory.prototype.qdrFromQuad = function (original) {
        return [
            original.subject,
            original.predicate,
            original.object,
            original.graph || this.defaultGraph(),
        ];
    };
    PlainFactory.prototype.qdrFromQdr = function (original) {
        return [
            original[0],
            original[1],
            original[2],
            original[3] || this.defaultGraph(),
        ];
    };
    /**
     * Compare if two RDF objects are the same.
     *
     * Should work with non-standard rdf libraries as well (e.g. supporting Variable and Collection).
     */
    PlainFactory.prototype.equals = function (a, b) {
        return equals(a, b);
    };
    PlainFactory.prototype.id = function (obj) {
        return this.toNQ(obj);
    };
    PlainFactory.prototype.toNQ = function (term) {
        if (Array.isArray(term)) {
            var graph = term[3] === this.defaultGraph() ? '' : (this.toNQ(term[3]) + ' ');
            return this.toNQ(term[0]) + " " + this.toNQ(term[1]) + " " + this.toNQ(term[2]) + " " + graph + ".";
        }
        else if (this.isQuad(term)) {
            return this.quadToNQ(term);
        }
        return this.termToNQ(term);
    };
    PlainFactory.prototype.parseLiteral = function (value) {
        var literalValue = undefined;
        var datatype = undefined;
        if (typeof value === "number" || value instanceof Number) {
            // From rdflib; https://github.com/linkeddata/rdflib.js/blob/master/src/literal.js#L104
            if (value.toString().indexOf('e') < 0 && Math.ceil(value) <= Number.MAX_SAFE_INTEGER) {
                datatype = Number.isInteger(value) ? datatypes.integer : datatypes.decimal;
            }
            else {
                datatype = datatypes.double;
            }
            literalValue = value.toString();
        }
        else if (typeof value === "boolean" || value instanceof Boolean) {
            literalValue = value.toString();
            datatype = datatypes.boolean;
        }
        else if (typeof value === "bigint") {
            datatype = datatypes.integer;
            literalValue = value.toString();
        }
        else if (typeof value === "object") {
            if (value instanceof Date) {
                if (isNaN(value.getTime())) {
                    throw new Error('Invalid date given');
                }
                literalValue = value.toISOString();
                datatype = datatypes.dateTime;
            }
        }
        if (typeof datatype !== "undefined") {
            return this.literal(literalValue, this.namedNode(datatype));
        }
        throw new Error("Can't parse value '" + value + "'");
    };
    PlainFactory.prototype.termToNQ = function (term) {
        switch (term.termType) {
            case TermType.BlankNode:
                return "_:" + term.value;
            case TermType.NamedNode:
                return "<" + term.value + ">";
            case TermType.Literal:
                if (term.datatype) {
                    if (term.datatype.value === datatypes.string) {
                        return "\"" + term.value + "\"";
                    }
                    return "\"" + term.value + "\"^^" + this.termToNQ(term.datatype);
                }
                if (term.language) {
                    return "\"" + term.value + "\"@" + term.language;
                }
                return "\"" + term.value + "\"";
            default:
                throw new Error("Nonstandard termtype '" + term.termType + "' given");
        }
    };
    PlainFactory.prototype.quadrupleToNQ = function (quad) {
        var graph = !quad[QuadPosition.graph] || quad[QuadPosition.graph] === this.defaultGraph()
            ? ''
            : (this.toNQ(quad[QuadPosition.graph]) + ' ');
        return this.toNQ(quad[QuadPosition.subject]) + " " + this.toNQ(quad[QuadPosition.predicate]) + " " + this.toNQ(quad[QuadPosition.object]) + " " + graph + ".\n";
    };
    PlainFactory.prototype.quadToNQ = function (quad) {
        return this.quadrupleToNQ([
            quad.subject,
            quad.predicate,
            quad.object,
            quad.graph || this.defaultGraph(),
        ]);
    };
    return PlainFactory;
}());
export { PlainFactory };
/**
 * Plain JS/functional implementation of the RDF/JS: Data model specification, limited to a strict
 * rdf subset (no Variable, no Literal as predicate, etc.).
 */
export var DefaultFactory = new PlainFactory();
